import { Alert, Stack, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import React, { useMemo } from 'react';
import { AlertCircle } from 'tabler-icons-react';

import { useGetCustomFields } from 'api/customFields';
import { User } from 'types';

import { CustomFieldInput } from './CustomFieldInput';

type CustomFieldsSectionProps = {
  form: UseFormReturnType<Record<string, unknown>>;
  orgId: string;
  projectId?: string;
  members?: User<string>[];
};

export function CustomFieldsSection({
  form,
  orgId,
  projectId,
  members = [],
}: CustomFieldsSectionProps) {
  const { data: customFields = [], isLoading } = useGetCustomFields(orgId, projectId);

  const customFieldsData = form.values.customFields as Record<string, unknown> | undefined;

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    customFields.forEach((field) => {
      if (field.required) {
        const value = customFieldsData?.[field.name];
        if (value === null || value === undefined) {
          errors.push(`${field.name} is required`);
        } else if (typeof value === 'string' && value.trim() === '') {
          errors.push(`${field.name} is required`);
        } else if (Array.isArray(value) && value.length === 0) {
          errors.push(`${field.name} is required`);
        }
      }
    });
    return errors;
  }, [customFields, customFieldsData]);

  if (isLoading) {
    return <Text size="sm" color="dimmed">Loading custom fields...</Text>;
  }

  if (customFields.length === 0) {
    return null;
  }

  return (
    <Stack spacing="md">
      <Text size="sm" weight={600}>
        Custom Fields
      </Text>
      {validationErrors.length > 0 && (
        <Alert icon={<AlertCircle size={16} />} color="red">
          {validationErrors.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </Alert>
      )}
      {customFields.map((field) => {
        const fieldId = field._id;
        return (
          <CustomFieldInput
            key={fieldId}
            field={field}
            form={form}
            members={members}
          />
        );
      })}
    </Stack>
  );
}
